// src/components/providerAssignments/ProviderWorkloadPanel.tsx
//
// Provider Workload panel — mounted in TeamMemberDetailSlideOver.
// Shows active workload counts + a summarized list of who / where
// / what role / priority. Gated by assignments.view_workload; the
// list itself needs assignments.view.

import { useMemo } from 'react';
import { Users } from 'lucide-react';
import { useHasCapability } from '../../services/centralContext';
import {
  useGetProviderWorkloadQuery,
  useListProviderAssignmentsQuery,
} from '../../api/providerAssignmentsApi';
import { useProviderAssignmentRealtime } from '../../hooks/useProviderAssignmentRealtime';
import type { PersonProviderAssignment } from '../../types/api';
import AssignmentsTable from './AssignmentsTable';

export interface ProviderWorkloadPanelProps {
  providerUserId: string;
  workspaceId?: string;
}

export default function ProviderWorkloadPanel({
  providerUserId,
  workspaceId,
}: ProviderWorkloadPanelProps) {
  const canViewWorkload = useHasCapability('assignments', 'view_workload');
  const canViewList = useHasCapability('assignments', 'view');

  useProviderAssignmentRealtime({ workspaceId, providerUserId });

  const { data: workload } = useGetProviderWorkloadQuery(providerUserId, {
    skip: !providerUserId || !canViewWorkload,
  });
  const { data: assignments = [] } = useListProviderAssignmentsQuery(
    { workspaceId, providerUserId, activeOnly: true },
    { skip: !providerUserId || !workspaceId || !canViewList },
  );

  const departments = useMemo(
    () => uniqueDepartmentNames(assignments),
    [assignments],
  );

  if (!canViewWorkload && !canViewList) return null;

  return (
    <section data-testid="provider-workload-panel" className="pt-4 border-t border-gray-200">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
        <Users className="h-4 w-4 text-gray-500" aria-hidden />
        Provider workload
      </h4>

      {canViewWorkload && workload && (
        <dl className="mb-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          <Stat label="Total active" value={workload.totalActive} />
          <Stat label="Primary" value={workload.primary} />
          <Stat label="Secondary" value={workload.secondary} />
          <Stat label="Backup" value={workload.backup} />
        </dl>
      )}

      {canViewList && (
        <>
          <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">
            Assignments
          </div>
          <AssignmentsTable
            rows={assignments}
            columns={['person', 'department', 'role', 'priority', 'status', 'startDate']}
            emptyText="No active assignments"
          />
          {departments.length > 0 && (
            <p className="mt-2 text-[11px] text-slate-500">
              Departments: {departments.join(', ')}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function uniqueDepartmentNames(rows: PersonProviderAssignment[]): string[] {
  const names = new Set<string>();
  for (const r of rows) if (r.departmentCircle?.name) names.add(r.departmentCircle.name);
  return Array.from(names).sort();
}
